import { css, html, LitElement, nothing } from 'lit'
import { customElement, query } from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { ExternalLinkElementModel, InternalLinkElementModel, MediaBlockElementModel, VideoSettingsElementModel } from '../api/ContentDeliveryApi.schemas'
import { renderMedia } from '../RenderElement'
import { Button, RenderButton } from '../Shared/Button'
import { RichTextStyles } from '../Shared/RichText'

@customElement('lindberg-media-block')
export class MediaBlock extends LitElement {

	static styles = [RichTextStyles, css`
		:host {
			display: flex;
			flex-direction: column;
			box-sizing: border-box;
			width: 100%;
		}		

		a, div {
			display: contents;
			color: inherit;
			text-decoration: none;
		}
		

		:host(.box-margin) div {
			display: block;
			margin: var(--elements-box-margins);
		}
		
		:host(.side-margin){
            padding-inline: var(--media-side-margins);
        }		

		:host(.side-box-margin){
            padding-inline: var(--elements-box-margins);
        }

		@media (width >= 600px) {
			:host(.two-column), :host(.three-column){
				grid-template-columns: repeat(12, 1fr);
				display: grid;
				a, div {
					display: block;
				}
			}		
			:host(.two-column) > *{
				grid-column: 3 / span 8;
			}
			:host(.three-column) > *{
				grid-column: 4 / span 6;
			}
		}
		
		:host(.center) {
			text-align: center;
		}

		:host(.left) {
			text-align: left;
		}

		:host(.right) {
			text-align: right;
		}

		:host(.middle){
            align-self: center;
        }

        :host(.top){
            align-self: start;
        }

        :host(.bottom){
            align-self: end;
        }

		:host(.fit-to-grid) * {
			&, * {height: 100%;}
		}

		lindberg-image:not(:last-child), lindberg-video:not(:last-child) {
			margin-bottom: var(--media-element-space);
		}

		lindberg-button {
			display: block;
			padding-top: var(--media-content-space);
			margin-top: auto;
		}

		@media (width >= 600px) {
            .mobile-only {
                display: none;
            }
        }

        @media (width < 600px) { 
            .desktop-only {
                display: none;
            }
        }
	`]

	data!: { content: MediaBlockElementModel; settings: VideoSettingsElementModel }

	@query('lindberg-button')
	cta?: Button

	override connectedCallback() {
		super.connectedCallback()
		if(this.data.settings?.properties?.margin) this.classList.add(this.data.settings.properties.margin.toLowerCase().split(" ").join("-"))		

		if(this.data.settings?.properties?.backgroundColor?.value) this.style.setProperty('background', this.data.settings.properties.backgroundColor.value)
		if(this.data.settings?.properties?.fitToGrid === true) this.classList.add('fit-to-grid')

		if (this.data.settings?.properties.contentAlign) {
			const [vertical, horizontal] = this.data.settings?.properties.contentAlign.split(' ')
            this.data.settings?.properties.contentAlign?.split(' ').forEach(x => this.classList.add(x))
		}
	}

	override onmouseenter = (e: MouseEvent) => {
		this.cta?.classList.add('hover');
	}

	override onmouseleave = (e: MouseEvent) => {
		this.cta?.classList.remove('hover');
	}
	
	render() {
		const link = this.data.content.properties.link?.content as InternalLinkElementModel | ExternalLinkElementModel
		if(link && link.properties.blockLink) {
			const url = link?.contentType === 'internalLink' ? link.properties.link?.route.path : link.properties.link
			const target = link?.contentType === 'externalLink' && link.properties.openInNewWindow ? '_blank' : nothing
			return html`<a href="${url}" target="${target}">${this.renderContent(true)}</a>`
		}
		return this.renderContent()
	}
	
	renderContent  = (inline: boolean = false) => {
		return html`
			${this.data.content.properties?.media ? renderMedia(this.data.content.properties.media, this.data.settings, this.data.content.properties?.mobileMedia ?? undefined) : nothing}
			${this.data.content.properties?.text?.markup || this.data.content.properties?.link ? html`<div>
				${this.data.content.properties?.text?.markup ? html`${unsafeHTML(this.data.content.properties.text.markup)}` : nothing}
				${this.data.content.properties?.link ? RenderButton(this.data.content.properties.link, inline) : nothing}				
			</div>` : nothing}
		`
	}
}
